import React from 'react';
import { Controller, ControllerProps, useFormContext } from 'react-hook-form';
import { FREIGHT_TYPE } from '@schuettflix/interfaces';
import { cn } from '@/shared/utils/cn.ts';
import { useTranslation } from 'react-i18next';
import { SelectCombobox } from '@/shared/ui/Combobox/SelectCombobox.tsx';

export const FREIGHT_TYPE_LABEL_NAME = {
    BULK_MATERIAL: 'product.productCharacteristics.freightType.bulkMaterial',
    RECYCLING_BULK_MATERIAL: 'product.productCharacteristics.freightType.recyclingBulkMaterial',
    NON_HAZARDOUS_WASTE: 'product.productCharacteristics.freightType.nonHazardousWaste',
    HAZARDOUS_WASTE: 'product.productCharacteristics.freightType.hazardousWaste',
    CONSTRUCTION_MATERIAL: 'product.productCharacteristics.freightType.constructionMaterial',
    RECYCLING_CONSTRUCTION_MATERIAL: 'product.productCharacteristics.freightType.recyclingConstructionMaterial',
    ASPHALT: 'product.productCharacteristics.freightType.asphalt',
    CONCRETE: 'product.productCharacteristics.freightType.concrete',
} as const satisfies Record<FREIGHT_TYPE, string>;

export type FreightTypeProps = Pick<ControllerProps, 'name' | 'defaultValue' | 'disabled'> & {
    variant?: 'md' | 'lg';
    dataTest?: string;
    className?: string;
};

export const FreightTypeInput = React.forwardRef<HTMLInputElement, FreightTypeProps>(
    ({ name = 'freightType', disabled, defaultValue, variant = 'lg', dataTest, className }, _ref) => {
        const { t } = useTranslation();
        const { control } = useFormContext();

        const options = (Object.keys(FREIGHT_TYPE_LABEL_NAME) as FREIGHT_TYPE[]).map(freightType => ({
            value: freightType,
            label: t(FREIGHT_TYPE_LABEL_NAME[freightType]),
        }));

        return (
            <Controller
                name={name}
                control={control}
                defaultValue={defaultValue ?? null}
                render={({ field: { ref, value, onChange }, fieldState }) => (
                    <SelectCombobox
                        fieldRef={ref}
                        value={value ?? undefined}
                        onValueChange={freightType => onChange(freightType)}
                        options={options}
                        disabled={disabled}
                        placeholder={t('product.quote.freightType')}
                        error={fieldState.error?.message}
                        size={variant}
                        dataTest={dataTest}
                        className={cn('flex-grow', className)}
                    />
                )}
            />
        );
    }
);
